/* @flow */
import type { Action } from "../action";
import type { StockID } from "../CommonTypes";

export type State = {
  isCryptoAvailable: boolean,
  isCryptoDismissed: boolean,
  isCryptoAppropriate: boolean,
  tabs: Array<StockID>,
  activeTab: number
};

export const TABS_LIMIT = 4;

function setActiveStock(state: State, payload ) {
  const {
    newStock: stockID
  } = payload;

  if (typeof stockID !== "string") {
    throw new TypeError("StockID must be specified");
  }

  const currentStock = state.tabs[state.activeTab];

  const isCryptoDismissed =
    currentStock === "CRYPTOINDEX" ? true : state.isCryptoDismissed;

  const tabs = state.tabs.slice();
  tabs.splice(state.activeTab, 1, stockID);

  return {
    ...state,
    tabs,
    isCryptoDismissed
  };
}

function switchToStock(state, stock) {
  let index = state.tabs.indexOf(stock);

  if (index === -1) {
    return setActiveStock(state, { newStock: stock });
  } else {
    return {
      ...state,
      activeTab: index
    };
  }
}

function removeTab(state: State, index?: number) {
  if (typeof index !== "number") {
    throw new TypeError("Tab index must be a number");
  }

  // Бездействуем, если остался всего один таб,
  // или удаляемый индекс не попадает в набор.
  if (state.tabs.length <= 1 || index < 0 || state.tabs.length <= index) {
    return state;
  }

  const stock = state.tabs[index];

  const isCryptoDismissed =
    stock === "CRYPTOINDEX" ? true : state.isCryptoDismissed;

  // Режем табы на две части: до и после
  // удаляемого элелента.
  const prefix = state.tabs.slice(0, index);
  const suffix = state.tabs.slice(index + 1);

  if (index === state.activeTab) {
    // Если удаляем активный таб, то нужно
    // грамотно передать активность на соседний.
    return {
      tabs: [...prefix, ...suffix],
      activeTab: suffix.length > 0 ? prefix.length : prefix.length - 1,
      isCryptoDismissed
    };
  } else {
    // Если удаляем таб, который стоит перед активным,
    // то надо учесть смещение его индекса.
    return {
      tabs: [...prefix, ...suffix],
      activeTab:
        state.activeTab < prefix.length ? state.activeTab : state.activeTab - 1,
      isCryptoDismissed
    };
  }
}

function receiveSocketMessage(state, payload) {
  switch (payload.op) {
    case "operationOpened":
      if (state.tabs.includes(payload.stock)) {
        return state;
      }

      return {
        ...state,
        tabs: [...state.tabs, payload.stock]
      };
    default:
      return state;
  }
}

export function receiveStockAssets(state, payload) {
  let isCryptoAvailable = false;
  let defaultTab = null;
  let pickOfTheDayTab = null;

  // В списке доступных стоков можен не оказаться тех, которые
  // пользователь выбирал ранее. Нам надо их отфильтровать. Для
  // этого сначала мы соберём список доступных.

  const currentTabs = state.tabs;
  const allowedTabs = new Set();

  const categories = Object.keys(payload);

  for (const category of categories) {
    const assets = payload[category];

    for (const asset of assets) {
      allowedTabs.add(asset.stock);

      if (asset.stock === "CRYPTOINDEX" && asset.enabled) {
        isCryptoAvailable = true;
      }

      if (defaultTab == null && asset.default && asset.enabled) {
        defaultTab = asset.stock;
      }

      if (pickOfTheDayTab == null && asset.pickOfTheDay && asset.enabled) {
        pickOfTheDayTab = asset.stock;
      }
    }
  }

  // Актив дня преобладает над активом по-умолчанию в праве занять первый таб у
  // нового клиента.

  defaultTab = pickOfTheDayTab || defaultTab;

  let nextTabs = currentTabs.filter(tab => allowedTabs.has(tab));

  if (defaultTab == null) {
    defaultTab = [...allowedTabs][0] || "EURUSD";
  }

  if (nextTabs.length === 0) {
    nextTabs = [defaultTab];
  }

  return {
    ...state,
    isCryptoAvailable,
    tabs: nextTabs,
    activeTab: Math.min(state.activeTab, nextTabs.length - 1)
  };
}

function receivePreservedTabs(state, preservedState) {
  // Мы (зря) полагаемся на то, что localStorage синхронный,
  // и сохранённые табы обязательно появятся раньше любых апи
  // ответов. В будущем механика может поменяться.
  // Если ты читаешь это когда она поменялась (например, табы
  // теперь сохраняются на сервере), то в receiveStockAssets
  // тебе придётся сохранять список доступных пар, чтобы позже
  // пересечь его со списком табов пользователя.
  // Я не сделал этого заранее потому, что в текущей ситуации
  // весь список стоков сохранится в LS, а я этого не хочу.

  return preservedState;
}

export const initialState: State = {
  isCryptoAvailable: false,
  isCryptoDismissed: false,
  tabs: [],
  activeTab: 0
};

function tabs(state: State = initialState, action: Action): State {
  switch (action.type) {
    case "RECEIVE_SOCKET_MESSAGE":
      return receiveSocketMessage(state, action.payload);
    case "RECEIVE_TABS":
      return receivePreservedTabs(state, action.payload);
    case "SET_ACTIVE_STOCK":
      return setActiveStock(state, action.payload);
    case "SET_ACTIVE_TAB":
      return { ...state, activeTab: action.payload };
    case "PUSH_TAB":
      if (typeof action.payload !== "string") {
        throw new TypeError("StockID must be specified");
      }
      return {
        activeTab: state.tabs.length,
        tabs: [...state.tabs, action.payload]
      };
    case "REMOVE_TAB":
      return removeTab(state, action.payload);
    case "RECEIVE_STOCK_ASSETS":
      return receiveStockAssets(state, action.payload);
    case "SWITCH_TO_STOCK":
      return switchToStock(state, action.payload);
    default:
      return state;
  }
}

export default tabs;

export function getViewedStock(state: State): string {
  return state.tabs[state.activeTab];
}



// WEBPACK FOOTER //
// ./src/redux/reducer/tabs.js